import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import classNames from "classnames";
import React from "react";

import { FieldLabel } from "../../../../components/newUI/text";
import { formatPhoneInput } from "../../utils/phoneNumber";

export type ContactKind = "Client" | "Prospect" | "Other";

export const CONTACT_KIND_OPTIONS: ContactKind[] = [
  "Client",
  "Prospect",
  "Other",
];

type ContactTypeSelectorProps = {
  value: ContactKind;
  onChange: (value: ContactKind) => void;
};

export const ContactTypeSelector: React.FC<ContactTypeSelectorProps> = ({
  value,
  onChange,
}) => {
  return (
    <div className={"mt-5"}>
      <FieldLabel label={"Contact Type"} />
      <div className={"flex gap-4"}>
        {CONTACT_KIND_OPTIONS.map((option) => (
          <div key={option} className={"form-control w-fit"}>
            <button
              type={"button"}
              onClick={() => onChange(option)}
              className={classNames(
                "flex h-[30px] w-[100px] items-center justify-center rounded-lg border-none px-4 py-2 text-[14px] font-semibold outline-none disabled:opacity-75",
                value === option
                  ? "text-purple bg-[#ededfe] hover:bg-[#aaa0e0]"
                  : "bg-white text-gray-400 hover:bg-gray-200",
              )}>
              {option}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

/** Phone box formatting (QA batch 12: keeps +1 / international numbers right). */
export const formatPhoneNumber = (value: string): string =>
  formatPhoneInput(value);

export const formatSin = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  const parts = [
    digits.slice(0, 3),
    digits.slice(3, 6),
    digits.slice(6, 9),
  ].filter(Boolean);

  return parts.join("-");
};

export const parseMutationError = (error: unknown): string => {
  if (!error || typeof error !== "object") {
    return "Something went wrong";
  }

  const fetchError = error as FetchBaseQueryError & {
    data?: unknown;
    error?: string;
  };

  if (typeof fetchError.data === "string") {
    const trimmed = fetchError.data.trim();

    if (!trimmed || trimmed.toLowerCase() === "null") {
      return "Something went wrong";
    }

    const meaningfulPart = trimmed
      .split(":")
      .map((part) => part.trim())
      .filter((part) => part && part.toLowerCase() !== "null")
      .pop();

    return meaningfulPart || trimmed;
  }

  if (fetchError.data && typeof fetchError.data === "object") {
    const data = fetchError.data as { message?: unknown; error?: unknown };
    const message = data.message || data.error;

    if (typeof message === "string" && message.trim()) {
      return message.trim();
    }
  }

  if (typeof fetchError.error === "string" && fetchError.error.trim()) {
    return fetchError.error.trim();
  }

  return "Something went wrong";
};
